/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Lead management page: a hero card with the 预约到店 primary CTA (and, on
 * desktop, inline 标记流失 / 取消商机), covered items with the customer
 * fix/decline decision, and the activity timeline whose header carries the
 * 跟进记录 button. On mobile, 标记流失 / 取消商机 fold behind the nav-bar
 * three-dots menu. Thin composition root: fetch, wire actions, lay out sections.
 */

import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { NavHeaderMoreButton } from '@euler/app/components/nav/NavHeaderMoreButton';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { LeadBundleSheet } from '@euler/app/flows/feed/components/LeadBundleSheet';
import { LeadsNavParams } from '@euler/app/flows/leads';
import { useLeadCenter } from '@euler/app/flows/leads/LeadCenterContext';
import { CustomerInfoEditor } from '@euler/app/flows/leads/pages/detail/components/CustomerInfoEditor';
import { LeadActionRow } from '@euler/app/flows/leads/pages/detail/components/LeadActionRow';
import { LeadHeaderCard } from '@euler/app/flows/leads/pages/detail/components/LeadHeaderCard';
import { VisibilityPicker } from '@euler/app/flows/leads/pages/detail/components/VisibilityPicker';
import { CoveredItemsSection } from '@euler/app/flows/leads/pages/detail/components/covered-items-section';
import { LeadDiscussionSection } from '@euler/app/flows/leads/pages/detail/components/discussion-section';
import { FollowUpSection } from '@euler/app/flows/leads/pages/detail/components/follow-up-section';
import { LastServiceCard } from '@euler/app/flows/leads/pages/detail/components/last-service-card';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import {
  isLeadClosingActivity,
  renderLeadActivityTitle,
} from '@euler/app/flows/leads/pages/detail/functions/renderLeadActivity';
import { useLeadActions } from '@euler/app/flows/leads/pages/detail/functions/useLeadActions';
import { useLeadDetail } from '@euler/app/flows/leads/pages/detail/functions/useLeadDetail';
import { useReservationCenter } from '@euler/app/flows/reservations/provider';
import { Center } from '@euler/components/Center';
import { DateTimePicker } from '@euler/components/DateTimePicker';
import { EmptyView } from '@euler/components/EmptyView';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { OrgUserRoleType } from '@euler/model/enum';
import { isTerminalLeadStatus } from '@euler/model/lead/Lead';
import { dateFromValue } from '@euler/utils/datetime';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useRef } from 'react';
import { ScrollView, View } from 'react-native';

export const LeadDetailScreen = wrapNavigatorScreen(
  ({ id }: { id: string }) => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();
    const navigation = useNavigation<StackNavigationProp<LeadsNavParams>>();
    const currentUser = useCurrentUser();

    const query = useLeadDetail(id);
    const detail = query.state;
    // Compute terminality before any early return so the header-options effect
    // below stays unconditional (rules of hooks). Treat the loading state as
    // terminal so the "more" affordance doesn't flash before the lead loads.
    const terminal = detail ? isTerminalLeadStatus(detail.lead.status) : true;
    // A booked lead is managed through its reservation — cancelling there flips
    // the lead back — so the lead page hides its own reserve / lose / cancel
    // actions while booked.
    const booked = detail?.lead.status === 'booked';

    // Refresh when returning from the log / book sub-screens. Depend on the
    // stable `invalidate` (a persistFn) — `query` is a fresh object literal
    // every render, so `[query]` would re-run the focus effect on every render
    // and spin an infinite refetch loop.
    const focusedOnce = useRef(false);
    const invalidate = query.invalidate;
    useFocusEffect(
      useCallback(() => {
        if (focusedOnce.current) {
          void invalidate();
        } else {
          focusedOnce.current = true;
        }
      }, [invalidate]),
    );

    const actions = useLeadActions(
      id,
      invalidate,
      detail?.lead.assigneeId,
      detail?.lead,
    );

    // When this lead's reservation is cancelled from the embedded reservation
    // detail (which pops itself back here), refresh the lead — the backend
    // reverts it to `working` — so the detail and, via the lead-center emit, the
    // list card both reflect the change.
    const { reservationCanceled$ } = useReservationCenter();
    useEffect(() => {
      const sub = reservationCanceled$.subscribe(reservation => {
        if (reservation.leadId === id) void invalidate();
      });
      return () => sub.unsubscribe();
    }, [reservationCanceled$, id, invalidate]);

    // Broadcast the latest lead so the list (or any sibling) can patch its card
    // in place. Skip the initial load (nothing changed yet); refetches triggered
    // by a mutation carry the new values. Decoupled from nav params so a
    // deep-link straight into this screen still works.
    const { emitLeadUpdated } = useLeadCenter();
    const pushedInitialRef = useRef(false);
    const currentLead = detail?.lead;
    useEffect(() => {
      if (!currentLead) return;
      if (!pushedInitialRef.current) {
        pushedInitialRef.current = true;
        return;
      }
      emitLeadUpdated(currentLead);
    }, [currentLead, emitLeadUpdated]);

    useEffect(() => {
      navigation.setOptions({
        headerRight:
          isDesktop || terminal || booked
            ? undefined
            : () => (
                <NavHeaderMoreButton
                  color={LD.ink}
                  size={rpx(18)}
                  onPress={actions.openMoreMenu}
                  style={{ marginRight: rpx(8) }}
                />
              ),
      });
    }, [navigation, isDesktop, terminal, booked, actions.openMoreMenu, rpx]);

    if (query.loading && detail == null) {
      return (
        <Center css={'flex: 1;'}>
          <DefaultLoader />
        </Center>
      );
    }
    if (detail == null) {
      return <EmptyView error={query.error} onRetry={query.invalidate} />;
    }

    const { lead, items, activities } = detail;

    // Only the creator, the current owner, or a store manager may change who can
    // see the lead. (Terminal leads can't be edited at all.)
    const myMemberId = currentUser?.user.memberId;
    const isStoreManager = currentUser
      ? new OrgUserRoleHelper(currentUser.user.role).hasRole(
          OrgUserRoleType.Manager,
        )
      : false;
    const canEditVisibility =
      !terminal &&
      (isStoreManager ||
        myMemberId === lead.createdBy ||
        myMemberId === lead.assigneeId);

    // A reserved lead links from its status pill to the reservation it booked.
    const reservationId = booked ? lead.reservationId : null;

    // The most recent staff outreach that actually reached out (a 备注 note is
    // an internal jotting, not a contact) — surfaced in the header so the
    // advisor sees the last touch without scrolling the timeline. Activities
    // arrive newest-first, so the first match is the latest.
    const lastReachActivity = activities.find(a => {
      const data = a.data;
      return (
        a.kind === 'manual' &&
        data?.type === 'outreach' &&
        data.channel !== 'note'
      );
    });
    const lastReach = lastReachActivity
      ? {
          summary: renderLeadActivityTitle(lastReachActivity),
          at: lastReachActivity.createdAt,
        }
      : null;
    // A closed lead leads with how it closed: the last outreach before it
    // ("考虑中") would read as if the customer were still deciding.
    const closingActivity = terminal
      ? activities.find(a => isLeadClosingActivity(a.data))
      : undefined;
    const closing = closingActivity
      ? {
          summary: renderLeadActivityTitle(closingActivity),
          at: closingActivity.createdAt,
        }
      : null;

    return (
      <View style={{ flex: 1, backgroundColor: LD.canvas }}>
        <ScrollView
          contentContainerStyle={{ padding: rpx(12), paddingBottom: rpx(28) }}
        >
          <LeadHeaderCard
            lead={lead}
            items={items}
            terminal={terminal}
            lastReach={lastReach}
            closing={closing}
            onEditCustomer={actions.customer.open}
            onEditIntention={!terminal ? actions.intention.open : undefined}
            onEditOwner={!terminal ? actions.owner.open : undefined}
            onEditTargetDate={!terminal ? actions.targetDate.open : undefined}
            onEditVisibility={
              canEditVisibility ? actions.visibility.open : undefined
            }
            onOpenReservation={
              reservationId
                ? () =>
                    navigation.navigate('_reservation', { id: reservationId })
                : undefined
            }
          />

          {!terminal && !booked ? (
            <LeadActionRow
              isDesktop={isDesktop}
              onReserve={() =>
                navigation.navigate('createReservation', { leadId: id })
              }
              onLose={() => void actions.runTransition('lose')}
              onCancel={() => void actions.runTransition('cancel')}
            />
          ) : null}

          <CoveredItemsSection
            items={items}
            terminal={terminal}
            onAdd={!terminal ? actions.addItems.openMenu : undefined}
            onItemAction={actions.openItemActions}
          />

          {detail.lastService ? (
            <LastServiceCard info={detail.lastService} />
          ) : null}

          <FollowUpSection
            activities={activities}
            terminal={terminal}
            onAddActivity={() =>
              navigation.navigate('logActivity', { leadId: id })
            }
          />

          <LeadDiscussionSection leadId={id} />
        </ScrollView>

        <LeadBundleSheet
          show={actions.addItems.shown}
          leadId={id}
          anchorDate={lead.targetServiceDate}
          submitting={actions.addItems.submitting}
          pinTarget={false}
          title="添加保养项"
          submittingLabel="添加中…"
          onClose={actions.addItems.close}
          onConfirm={actions.addItems.confirm}
        />

        <CustomerInfoEditor
          visible={actions.customer.editing}
          initialName={lead.customerName ?? ''}
          initialPhone={lead.customerPhone ?? ''}
          onClose={actions.customer.close}
          onSave={actions.customer.save}
        />

        <VisibilityPicker
          visible={actions.visibility.editing}
          value={lead.visibility}
          onClose={actions.visibility.close}
          onSelect={actions.visibility.save}
        />

        <DateTimePicker
          isVisible={actions.targetDate.editing}
          mode="date"
          date={
            lead.targetServiceDate
              ? dateFromValue(lead.targetServiceDate)
              : new Date()
          }
          onConfirm={date => void actions.targetDate.save(date)}
          onCancel={actions.targetDate.close}
          confirmTextIOS="确认"
          cancelTextIOS="关闭"
        />
      </View>
    );
  },
  {
    title: '商机详情',
  },
);
